import axios from 'axios';
import { BASE_URL } from './helpers';
import { CompositeInferenceProvider, LoRAAdapterArtifact } from './loraRuntime';
import { INFERENCE_PROVIDER, normalizeFamily } from './modelFamily';

// ==================== Types (pinned backend contracts) ====================

export type GeneratorProfileStatus = 'draft' | 'published';
export type GeneratorProfileMediaType = 'image';

/**
 * The LoRA model family. SDXL is the long-standing default; z-image is the
 * Tongyi-MAI Z-Image architecture. An omitted/empty family means sdxl for
 * back-compat — REQUIRED when the base source is 'civitai' (a civitai id alone
 * cannot tell us the architecture).
 */
export type ModelFamily = 'sdxl' | 'z-image';

export interface GeneratorProfileBaseModel {
  source: 'hf' | 'civitai';
  /** HF id, or `civitai:<modelId>@<versionId>` when source is 'civitai'. */
  id: string;
  /** Omitted/empty means sdxl. Required for civitai bases. */
  family?: ModelFamily;
}

export interface GeneratorProfileAdapter {
  artifact_id: string;
  weight: number;
  /** Omitted/undefined means enabled (back-compat); only an explicit `false` disables it. */
  enabled?: boolean;
}

/** A textual-inversion embedding artifact attached to the profile. */
export interface GeneratorProfileEmbedding {
  artifact_id: string;
  kind: 'positive' | 'negative';
  token: string;
  weight?: number;
}

/** Overrides the base checkpoint's baked-in VAE. */
export interface GeneratorProfileVAE {
  source: 'hf';
  id: string;
}

export interface GeneratorProfileFaceDetailer {
  enabled: boolean;
  strength: number;
  denoise: number;
  max_faces: number;
}

export interface GeneratorProfileFaceRestore {
  enabled: boolean;
  fidelity: number;
}

/** Post-processing steps applied after sampling. */
export interface GeneratorProfilePost {
  face_detailer?: GeneratorProfileFaceDetailer;
  face_restore?: GeneratorProfileFaceRestore;
  debug?: boolean;
}

export interface GeneratorProfileUIPos {
  x: number;
  y: number;
}

/** Editor-only layout hints (e.g. control-node canvas positions) persisted alongside the spec. */
export interface GeneratorProfileUI {
  layout?: Record<string, GeneratorProfileUIPos>;
}

export type GeneratorProfileControlType = 'openpose' | 'softedge' | 'depth';
export type GeneratorProfileControlPreset = 'safe_pose' | 'exact_shape';
export type GeneratorProfileControlSchedule = 'constant' | 'structure_first';

export type PuppetPosePreset = 'neutral' | 'sitting' | 'walking' | 'arms_up' | 'custom';
export type PuppetComposition = 'full_body' | 'feet_only';
export type PuppetFootMode = 'pair' | 'left' | 'right';
export type PuppetFootPreset =
  | 'paired_soles' | 'relaxed_soles' | 'toes_spread' | 'toes_curled'
  | 'top_view' | 'standing' | 'tiptoe' | 'dorsiflexed'
  | 'left_profile' | 'right_profile' | 'crossed_ankles'
  | 'one_forward' | 'single_sole' | 'single_top' | 'custom';
export type PuppetJointName =
  | 'nose' | 'neck' | 'rShoulder' | 'rElbow' | 'rWrist'
  | 'lShoulder' | 'lElbow' | 'lWrist' | 'rHip' | 'rKnee'
  | 'rAnkle' | 'lHip' | 'lKnee' | 'lAnkle'
  | 'rEye' | 'lEye' | 'rEar' | 'lEar';
export interface PuppetJointOffset { x: number; y: number }

export type PuppetBodyType = 'neutral' | 'female';

export interface PuppetBodyProfile {
  shoulder_width: number;
  hip_width: number;
  torso_length: number;
  arm_length: number;
  leg_length: number;
  head_scale: number;
  body_type: PuppetBodyType;
  breast_size: number;
  /** Shoulder-to-hip fraction of the breast center; larger values move it down. */
  breast_position: number;
}

export interface PuppetSpatialConfig {
  enabled: boolean;
  camera_yaw: number;
  camera_pitch: number;
  camera_fov: number;
  body_depth: number;
  /** Positive values move joints toward the camera; values are normalized. */
  joint_depths?: Partial<Record<PuppetJointName, number>>;
}

export interface PuppetFootSideConfig {
  enabled: boolean;
  surface: 'sole' | 'top';
  /** Foot center within the normalized feet-only stage. */
  position_x: number;
  position_y: number;
  /** Camera-relative orientation in degrees. */
  yaw: number;
  pitch: number;
  roll: number;
  ankle_flex: number;
  inversion: number;
  length: number;
  width: number;
  heel_width: number;
  ball_width: number;
  arch_height: number;
  toe_spread: number;
  toe_curl: number;
  big_toe_angle: number;
  /** Hallux through little toe; anatomy lock preserves this ordering. */
  toe_lengths: [number, number, number, number, number];
}

export interface PuppetFeetConfig {
  enabled: boolean;
  mode: PuppetFootMode;
  preset: PuppetFootPreset;
  linked: boolean;
  anatomy_lock: boolean;
  strict_anatomy: boolean;
  /** Mirrors the authored guide vertically so the toes point down. */
  toes_down: boolean;
  framing_scale: number;
  lower_leg_visibility: number;
  left: PuppetFootSideConfig;
  right: PuppetFootSideConfig;
}

export type PuppetFootLandmarkName =
  | 'lAnkle' | 'lHeel' | 'lBigToe' | 'lSmallToe'
  | 'rAnkle' | 'rHeel' | 'rBigToe' | 'rSmallToe';

export interface PuppetGuideConfig {
  source: 'puppet';
  renderer_version: 1 | 2 | 3 | 4 | 5;
  /** v5 can render a dedicated feet-only ControlNet guide without a body. */
  composition?: PuppetComposition;
  pose_preset: PuppetPosePreset;
  /** Stable library identifier. Additive so legacy profiles keep working. */
  pose_id?: string;
  /** User-facing name only; it is never injected into a generation prompt. */
  pose_name?: string;
  /** Explicit, bounded semantic description that may be added to the prompt. */
  pose_prompt_hint?: string;
  pose_definition_version?: 1;
  body_profile: PuppetBodyProfile;
  /** Additive renderer-v4 state for a procedural 3D puppet/depth guide. */
  spatial?: PuppetSpatialConfig;
  /** Additive renderer-v5 anatomical foot authoring state. */
  feet?: PuppetFeetConfig;
  /** Optional COCO-WholeBody/DWPose-style detail layers for OpenPose guides. */
  include_face_keypoints?: boolean;
  include_hand_keypoints?: boolean;
  joint_offsets?: Partial<Record<PuppetJointName, PuppetJointOffset>>;
}

export interface PuppetPoseLibraryItem {
  id: string;
  user_id: string;
  name: string;
  prompt_hint: string;
  config: PuppetGuideConfig;
  created_at: string;
  updated_at: string;
}

export interface GeneratorProfileControl {
  id: string;
  type: GeneratorProfileControlType;
  /** Z-Image only: Lite pose guidance or full depth/proportion guidance. */
  conditioning_preset?: GeneratorProfileControlPreset;
  control_schedule?: GeneratorProfileControlSchedule;
  /** Empty means the Modal Bridge resolves its approved server-side model. */
  model_id?: string;
  input_role: 'pose_guide' | 'body_guide' | 'depth_guide';
  weight: number;
  start: number;
  end: number;
  required: boolean;
  /** Reproducible authoring parameters; concrete PNG bytes remain run-scoped. */
  guide_config?: PuppetGuideConfig;
}

/** SDXL image-prompt condition. Modal owns the concrete approved model. */
export interface GeneratorProfileIPAdapter {
  input_role: 'ip_adapter_reference';
  scale: number;
  required: boolean;
}

export interface GeneratorProfilePrompt {
  base_prompt: string;
  negative_prompt: string;
  /** Opt-in `(phrase:weight)` conditioning for SDXL and Z-Image. */
  weighting_enabled?: boolean;
}

/**
 * Inference path. txt2img generates from noise; img2img reworks a seed image;
 * inpaint reworks only the masked region of a seed image. Inpaint is supported
 * for BOTH model families (per the Task-14 z-image spike, which found the
 * pinned diffusers ships `ZImageInpaintPipeline` directly) — unlike
 * embeddings/vae/clip_skip, which the bridge gates to SDXL only.
 */
export type GeneratorProfileMode = 'txt2img' | 'img2img' | 'inpaint';

export interface GeneratorProfileParams {
  width: number;
  height: number;
  steps: number;
  cfg_scale: number;
  scheduler: string;
  /** Inference path. txt2img (default) generates from noise; img2img reworks a seed image. */
  mode: GeneratorProfileMode;
  /**
   * How much the seed image is altered in img2img, in (0,1]. Low values preserve
   * the seed's composition; high values reinvent it. The backend defaults to 0.65.
   */
  denoise_strength: number;
  /** Reproducible seed; omitted lets the bridge draw one. */
  seed?: number;
  /** CLIP skip layer count; omitted uses the backend default. */
  clip_skip?: number;
}

export interface GeneratorProfileSpec {
  base_model: GeneratorProfileBaseModel;
  adapters: GeneratorProfileAdapter[];
  controls?: GeneratorProfileControl[];
  ip_adapter?: GeneratorProfileIPAdapter;
  prompt: GeneratorProfilePrompt;
  params: GeneratorProfileParams;
  embeddings?: GeneratorProfileEmbedding[];
  vae?: GeneratorProfileVAE;
  post?: GeneratorProfilePost;
  ui?: GeneratorProfileUI;
}

export interface GeneratorProfile {
  id: string;
  slug: string;
  version: number;
  status: GeneratorProfileStatus;
  user_id: string;
  name: string;
  media_type: GeneratorProfileMediaType;
  spec: GeneratorProfileSpec;
  created_at: string;
  updated_at: string;
}

export interface CreateGeneratorProfileRequest {
  slug: string;
  name: string;
  media_type: GeneratorProfileMediaType;
  spec: GeneratorProfileSpec;
}

export interface UpdateGeneratorProfileRequest {
  name?: string;
  spec?: GeneratorProfileSpec;
}

export interface ListGeneratorProfilesParams {
  status?: GeneratorProfileStatus;
  slug?: string;
}

// ==================== Test generation (POST /lora/inference/generate) ====================

export type SchedulerOption =
  | 'default'
  | 'euler'
  | 'euler_a'
  | 'dpmpp_2m'
  | 'dpmpp_2m_karras'
  | 'unipc';

/**
 * Ties a test generation back to the profile it was run from. When present and
 * the run completes, the backend persists the first sample into the media
 * library tagged with these fields (metadata: generator_profile_id/_slug/_version,
 * with_adapters) and returns `persisted_media_ids` on the result — which is what
 * lets GenerationsRail show the profile's test history.
 */
export interface ProfileTestContext {
  profile_id: string;
  slug: string;
  version: number;
  with_adapters: boolean;
}

export interface ProfileTestGenerationRequest {
  prompt: string;
  negative_prompt: string;
  /** Parse explicit `(phrase:weight)` spans instead of treating them as literal text. */
  prompt_weighting?: boolean;
  base_model_id: string;
  /** Model family; selects the inference provider. Omitted → sdxl. */
  family?: ModelFamily;
  adapter_ids: string[];
  lora_strengths: number[];
  width: number;
  height: number;
  steps: number;
  cfg_scale: number;
  scheduler: string;
  seed?: number | null;
  /** Defaults to txt2img. img2img/inpaint require init_image_b64 to be set. */
  mode?: GeneratorProfileMode;
  /** Raw base64 seed image (no data-URI prefix) for img2img/inpaint tests. */
  init_image_b64?: string | null;
  /**
   * Existing Modal-volume image ref for img2img/inpaint tests (an alternative
   * to uploading fresh bytes via init_image_b64 — used by the admin composite
   * inference smoke tester). Ignored when init_image_b64 is also set.
   */
  init_image_ref?: string | null;
  denoise_strength?: number | null;
  /** Raw base64 mask image (no data-URI prefix) for inpaint tests — required alongside init_image_b64/ref when mode is 'inpaint'. */
  mask_image_b64?: string | null;
  /** Existing Modal-volume mask ref, an alternative to mask_image_b64 (admin smoke tester). */
  mask_image_ref?: string | null;
  /** Textual-inversion embeddings (SDXL only — the bridge rejects these for z-image). */
  embeddings?: GeneratorProfileEmbedding[];
  /** VAE override id (SDXL only). */
  vae_id?: string | null;
  /** CLIP skip layer count, 1..4 (SDXL only). */
  clip_skip?: number | null;
  /** Post-generation face-detailer/face-restore chain. Allowed for BOTH families. */
  post?: GeneratorProfilePost;
  /** Optional GPU override for inference (e.g. 'L4', 'A10G'). Omitted → server default. */
  gpu?: string;
  controls?: Array<{
    type: GeneratorProfileControlType;
    conditioning_preset?: GeneratorProfileControlPreset;
    control_schedule?: GeneratorProfileControlSchedule;
    pose_preset?: PuppetPosePreset;
    pose_id?: string;
    pose_name?: string;
    pose_prompt_hint?: string;
    pose_definition_version?: number;
    guide_renderer_version?: number;
    /** Structured Puppet proportions used for Z-Image's automatic prompt suffix. */
    body_profile?: PuppetBodyProfile;
    /** Structured v5 feet-only authoring state for prompt and strict anatomy checks. */
    feet?: PuppetFeetConfig;
    model_id?: string;
    guide_image_b64: string;
    /** Deterministic puppet joints used only by the bounded pose-quality check. */
    target_joints?: Partial<Record<PuppetJointName, PuppetJointOffset>>;
    foot_landmarks?: Partial<Record<PuppetFootLandmarkName, PuppetJointOffset>>;
    weight: number;
    start: number;
    end: number;
  }>;
  /** SDXL-only image-prompt reference, independent from ControlNet guides. */
  ip_adapter?: {
    image_b64?: string;
    image_ref?: string;
    scale: number;
  };
  /** Present only when the profile being tested has been saved (has an id). */
  profile_context?: ProfileTestContext;
  /**
   * Pass-through overrides for the admin composite-inference smoke tester,
   * which can target the bridge's local CPU scaffold instead of real Modal
   * GPU inference. Omitted (the Generator Profile builder path) → provider
   * is derived from `family` and runtime_id defaults to the Modal runtime.
   */
  runtime_id?: string;
  provider?: CompositeInferenceProvider;
}

/**
 * Diagnostics surfaced on a failed test generation. The backend nests
 * the richest diagnostics under metadata.modal_result for Modal runs, so the
 * UI flattens both the top-level metadata and the modal_result block.
 */
export interface ProfileTestImageStats {
  luminance_mean?: number | string;
  dynamic_range?: number | string;
  near_black?: boolean;
  near_blank?: boolean;
  [key: string]: unknown;
}

export interface ProfileTestDiagnostics {
  failure_reason?: string;
  retry_reason?: string;
  vae_fix_model?: string;
  vae_fix_error?: string;
  image_stats?: ProfileTestImageStats;
  first_pass_stats?: ProfileTestImageStats;
  [key: string]: unknown;
}

/**
 * Wave 5 post-chain summary (CompositeInferenceResult.post — see
 * app/schemas.py). `null`/absent means the request had no `post` or the
 * deployed Modal app predates the post chain. `error` is set instead of
 * stages_run/faces_found/durations when the chain caught an exception
 * (the bridge still returns a normal completed generation — see
 * `_run_post_chain_safe`).
 */
export interface ProfileTestPostSummary {
  stages_run?: string[];
  stages_skipped?: Array<{
    stage?: string;
    reason?: string;
    required_free_mib?: number;
    free_mib?: number;
  }>;
  faces_found?: number;
  durations?: Record<string, number>;
  memory?: Array<Record<string, unknown>>;
  error?: string;
  [key: string]: unknown;
}

export interface ProfileTestGenerationResult {
  id: string;
  status: 'completed' | 'failed' | string;
  sample_refs: string[];
  metadata: Record<string, unknown>;
  /**
   * Settled generation cost in credits (metadata.cost_credits). Usually absent
   * on the immediate test response — the credit-settle is async and completes
   * after this returns — so it only surfaces once the persisted media item is
   * re-fetched (see GenerationsRail). Read defensively via `extractCostCredits`.
   */
  costCredits?: number;
  /** Media library ids persisted from this run — only set when profile_context was sent and the run completed. */
  persisted_media_ids?: string[];
  /** The runtime_id the request resolved to (bridge echoes it back). Mainly of interest to the admin composite-inference smoke tester. */
  runtime_id?: string;
  /** Post-chain summary — present only when the request carried `post`. */
  post?: ProfileTestPostSummary | null;
}

export interface ProfileTestPoseQualityAttempt {
  attempt?: number;
  control_weight?: number;
  pose_control_weight?: number;
  proportion_control_weight?: number;
  pose_threshold?: number;
  pose_status?: 'passed' | 'failed' | 'unavailable' | 'skipped' | string;
  pose_score?: number;
  pose_joint_score?: number;
  pose_angle_score?: number;
  pose_joint_errors?: Record<string, number>;
  pose_worst_joints?: string[];
  pose_target_joints?: Record<string, PuppetJointOffset>;
  pose_detected_joints?: Record<string, PuppetJointOffset>;
  pose_confidence?: number;
  pose_reason?: string;
  proportion_threshold?: number;
  proportion_status?: 'passed' | 'failed' | 'unavailable' | 'skipped' | string;
  proportion_score?: number;
  proportion_reason?: string;
  proportion_deltas?: Record<string, number>;
  foot_control_weight?: number;
  foot_threshold?: number;
  foot_status?: 'passed' | 'failed' | 'unavailable' | 'skipped' | string;
  foot_score?: number;
  foot_reason?: string;
  foot_pairing_status?: 'passed' | 'failed' | string;
  expected_foot_count?: number;
  detected_foot_count?: number;
  foot_side_scores?: Record<string, number>;
  foot_direction_angle_errors?: Record<string, number>;
  framing_status?: 'passed' | 'failed' | 'unavailable' | 'skipped' | string;
  framing_reason?: string;
  /** Bounded message from the remote CPU pose preprocessor; never image data. */
  preprocess_error_message?: string;
  quality_score?: number;
}

export interface ProfileTestPoseQualityGuard {
  enabled?: boolean;
  retried?: boolean;
  selected_attempt?: number;
  retry_reason?: string;
  retry_reasons?: string[];
  retry_skipped_reason?: string;
  prompt_hints_added?: string[];
  strict_anatomy?: boolean;
  accepted?: boolean;
  attempts?: ProfileTestPoseQualityAttempt[];
}

// ==================== Helpers ====================

const profilesPath = (suffix = '') => `${BASE_URL}/generator-profiles${suffix}`;
const puppetPosesPath = (suffix = '') => `${BASE_URL}/puppet-poses${suffix}`;

const numberOr = (value: unknown, fallback: number): number => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizePuppetGuide = (raw: unknown): PuppetGuideConfig | undefined => {
  const guide = (raw || {}) as Partial<PuppetGuideConfig>;
  if (guide.source !== 'puppet') return undefined;
  const body = (guide.body_profile || {}) as Partial<PuppetBodyProfile>;
  const scale = (value: unknown) => Math.max(0.7, Math.min(1.3, numberOr(value, 1)));
  const bodyType: PuppetBodyType = body.body_type === 'female' ? 'female' : 'neutral';
  const pose: PuppetPosePreset = ['neutral', 'sitting', 'walking', 'arms_up', 'custom'].includes(String(guide.pose_preset))
    ? guide.pose_preset as PuppetPosePreset
    : 'neutral';
  const detailsRequested = guide.include_face_keypoints === true || guide.include_hand_keypoints === true;
  const spatialRaw = guide.spatial as Partial<PuppetSpatialConfig> | undefined;
  const spatialRequested = Boolean(spatialRaw);
  const feetRaw = guide.feet as Partial<PuppetFeetConfig> | undefined;
  const feetRequested = guide.composition === 'feet_only' || Boolean(feetRaw?.enabled);
  // Missing versions belong to persisted legacy profiles. New guides are
  // created explicitly as v3 by defaultPuppetGuideConfig, so keep old data at
  // v2 unless the new detail flags clearly opt it into the DWPose renderer.
  const rawRendererVersion = numberOr(guide.renderer_version, feetRequested ? 5 : spatialRequested ? 4 : detailsRequested ? 3 : 2);
  const supportedRendererVersion: PuppetGuideConfig['renderer_version'] = [1, 2, 3, 4, 5].includes(rawRendererVersion)
    ? rawRendererVersion as PuppetGuideConfig['renderer_version']
    : detailsRequested ? 3 : 2;
  const rendererVersion: PuppetGuideConfig['renderer_version'] = feetRequested
    ? 5
    : spatialRequested
    ? 4
    : detailsRequested && supportedRendererVersion < 3
      ? 3
      : supportedRendererVersion;
  const rawOffsets = (guide.joint_offsets || {}) as Record<string, unknown>;
  const jointNames: PuppetJointName[] = [
    'nose', 'neck', 'rShoulder', 'rElbow', 'rWrist', 'lShoulder', 'lElbow', 'lWrist',
    'rHip', 'rKnee', 'rAnkle', 'lHip', 'lKnee', 'lAnkle', 'rEye', 'lEye', 'rEar', 'lEar',
  ];
  const jointOffsets = Object.fromEntries(jointNames.flatMap((name) => {
    const offset = rawOffsets[name] as Partial<PuppetJointOffset> | undefined;
    if (!offset) return [];
    const clampOffset = (value: unknown) => Math.max(-0.45, Math.min(0.45, numberOr(value, 0)));
    return [[name, { x: clampOffset(offset.x), y: clampOffset(offset.y) }]];
  })) as Partial<Record<PuppetJointName, PuppetJointOffset>>;
  const rawJointDepths = (spatialRaw?.joint_depths || {}) as Record<string, unknown>;
  const jointDepths = Object.fromEntries(jointNames.flatMap((name) => {
    if (!(name in rawJointDepths)) return [];
    return [[name, Math.max(-0.35, Math.min(0.35, numberOr(rawJointDepths[name], 0)))]];
  })) as Partial<Record<PuppetJointName, number>>;
  const footSide = (rawSide: unknown, side: 'left' | 'right'): PuppetFootSideConfig => {
    const value = (rawSide || {}) as Partial<PuppetFootSideConfig>;
    const footScale = (rawValue: unknown, fallback = 1) => Math.max(0.5, Math.min(1.5, numberOr(rawValue, fallback)));
    const toeDefaults: PuppetFootSideConfig['toe_lengths'] = [1, 0.92, 0.82, 0.72, 0.62];
    const rawToeLengths = Array.isArray(value.toe_lengths) ? value.toe_lengths : [];
    const toeLengths = toeDefaults.map((fallback, index) => footScale(rawToeLengths[index], fallback)) as PuppetFootSideConfig['toe_lengths'];
    return {
      enabled: value.enabled !== false,
      surface: value.surface === 'top' ? 'top' : 'sole',
      position_x: Math.max(-0.4, Math.min(0.4, numberOr(value.position_x, side === 'left' ? -0.19 : 0.19))),
      position_y: Math.max(-0.35, Math.min(0.35, numberOr(value.position_y, 0))),
      yaw: Math.max(-90, Math.min(90, numberOr(value.yaw, 0))),
      pitch: Math.max(-90, Math.min(90, numberOr(value.pitch, 0))),
      roll: Math.max(-45, Math.min(45, numberOr(value.roll, 0))),
      ankle_flex: Math.max(-45, Math.min(45, numberOr(value.ankle_flex, 0))),
      inversion: Math.max(-30, Math.min(30, numberOr(value.inversion, 0))),
      length: footScale(value.length),
      width: footScale(value.width),
      heel_width: footScale(value.heel_width),
      ball_width: footScale(value.ball_width),
      arch_height: footScale(value.arch_height),
      toe_spread: Math.max(0, Math.min(1.5, numberOr(value.toe_spread, 0.35))),
      toe_curl: Math.max(-1, Math.min(1, numberOr(value.toe_curl, 0))),
      big_toe_angle: Math.max(-20, Math.min(20, numberOr(value.big_toe_angle, 0))),
      toe_lengths: toeLengths,
    };
  };
  const footPresets: PuppetFootPreset[] = [
    'paired_soles', 'relaxed_soles', 'toes_spread', 'toes_curled', 'top_view', 'standing',
    'tiptoe', 'dorsiflexed', 'left_profile', 'right_profile', 'crossed_ankles', 'one_forward',
    'single_sole', 'single_top', 'custom',
  ];
  const footMode: PuppetFootMode = ['pair', 'left', 'right'].includes(String(feetRaw?.mode))
    ? feetRaw?.mode as PuppetFootMode : 'pair';
  const footPreset: PuppetFootPreset = footPresets.includes(feetRaw?.preset as PuppetFootPreset)
    ? feetRaw?.preset as PuppetFootPreset : 'paired_soles';
  return {
    source: 'puppet',
    renderer_version: rendererVersion,
    ...(feetRequested ? { composition: 'feet_only' as const } : {}),
    pose_preset: pose,
    ...(typeof guide.pose_id === 'string' && guide.pose_id.trim()
      ? { pose_id: guide.pose_id.trim().slice(0, 64) } : {}),
    ...(typeof guide.pose_name === 'string' && guide.pose_name.trim()
      ? { pose_name: guide.pose_name.trim().slice(0, 80) } : {}),
    ...(typeof guide.pose_prompt_hint === 'string' && guide.pose_prompt_hint.trim()
      ? { pose_prompt_hint: guide.pose_prompt_hint.trim().replace(/\s+/g, ' ').slice(0, 180) } : {}),
    ...(guide.pose_definition_version === 1 ? { pose_definition_version: 1 as const } : {}),
    body_profile: {
      shoulder_width: scale(body.shoulder_width),
      hip_width: scale(body.hip_width),
      torso_length: scale(body.torso_length),
      arm_length: scale(body.arm_length),
      leg_length: scale(body.leg_length),
      head_scale: scale(body.head_scale),
      body_type: bodyType,
      breast_size: Math.max(0.5, Math.min(1.5, numberOr(body.breast_size, 1))),
      breast_position: Math.max(0.25, Math.min(0.65, numberOr(body.breast_position, 0.4))),
    },
    ...(spatialRequested ? {
      spatial: {
        enabled: spatialRaw?.enabled !== false,
        camera_yaw: Math.max(-60, Math.min(60, numberOr(spatialRaw?.camera_yaw, 0))),
        camera_pitch: Math.max(-30, Math.min(30, numberOr(spatialRaw?.camera_pitch, 0))),
        camera_fov: Math.max(25, Math.min(70, numberOr(spatialRaw?.camera_fov, 45))),
        body_depth: scale(spatialRaw?.body_depth),
        ...(Object.keys(jointDepths).length ? { joint_depths: jointDepths } : {}),
      },
    } : {}),
    ...(feetRequested ? {
      feet: {
        enabled: true,
        mode: footMode,
        preset: footPreset,
        linked: feetRaw?.linked !== false,
        // Handedness/order constraints are intentionally fail-safe. Persisted
        // false values from experimental clients cannot disable anatomy lock.
        anatomy_lock: true,
        strict_anatomy: feetRaw?.strict_anatomy !== false,
        toes_down: feetRaw?.toes_down === true,
        framing_scale: Math.max(0.7, Math.min(1.35, numberOr(feetRaw?.framing_scale, 1))),
        // Renderer v5.1 is ankle-free; retain the legacy field only so older
        // persisted profiles remain readable.
        lower_leg_visibility: 0,
        left: footSide(feetRaw?.left, 'left'),
        right: footSide(feetRaw?.right, 'right'),
      },
    } : {}),
    ...(guide.include_face_keypoints === true ? { include_face_keypoints: true } : {}),
    ...(guide.include_hand_keypoints === true ? { include_hand_keypoints: true } : {}),
    ...(Object.keys(jointOffsets).length ? { joint_offsets: jointOffsets } : {}),
  };
};

/** Clamp a denoise strength into the backend's accepted (0,1] range, defaulting to 0.65. */
export const clampDenoise = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 0.65;
  return Math.min(1, value);
};

/**
 * Strips a `data:<mime>;base64,` prefix from an encoded image, leaving raw base64.
 * The bridge tolerates the prefix, but we send raw base64 to keep payloads lean.
 */
export const stripDataUriPrefix = (value: string): string => {
  const comma = value.indexOf(',');
  return value.startsWith('data:') && comma >= 0 ? value.slice(comma + 1) : value;
};

/**
 * Normalizes one embedding entry. Requires a non-empty `artifact_id` (entries
 * missing it are meaningless — dropped, mirroring the adapters/controls
 * precedent); every other property — known or not — is preserved verbatim via
 * the spread so a future backend field is never silently stripped.
 */
const normalizeEmbedding = (raw: unknown): GeneratorProfileEmbedding | null => {
  if (!raw || typeof raw !== 'object') return null;
  const embedding = raw as Partial<GeneratorProfileEmbedding> & Record<string, unknown>;
  if (typeof embedding.artifact_id !== 'string' || !embedding.artifact_id) return null;
  return {
    ...embedding,
    artifact_id: embedding.artifact_id,
    kind: embedding.kind === 'negative' ? 'negative' : 'positive',
    token: typeof embedding.token === 'string' ? embedding.token : '',
  } as GeneratorProfileEmbedding;
};

/** VAE override is pass-through-only: `source` is coerced to the sole known value, everything else preserved. */
const normalizeVAE = (raw: unknown): GeneratorProfileVAE | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const vae = raw as Partial<GeneratorProfileVAE> & Record<string, unknown>;
  if (typeof vae.id !== 'string' || !vae.id) return undefined;
  return { ...vae, source: 'hf', id: vae.id } as GeneratorProfileVAE;
};

// Fallback defaults mirror the bridge's own FaceDetailerInput clamps/defaults
// (strength 0..1.2=0.95, denoise 0.1..0.8=0.45, max_faces 1..8=4 — see
// app/schemas.py) so a partially-specified stage normalizes to the same
// values the bridge would apply, not an arbitrary client-side guess.
const normalizeFaceDetailer = (raw: unknown): GeneratorProfileFaceDetailer | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const detailer = raw as Partial<GeneratorProfileFaceDetailer> & Record<string, unknown>;
  return {
    ...detailer,
    enabled: Boolean(detailer.enabled),
    strength: numberOr(detailer.strength, 0.95),
    denoise: numberOr(detailer.denoise, 0.45),
    max_faces: numberOr(detailer.max_faces, 4),
  } as GeneratorProfileFaceDetailer;
};

const normalizeFaceRestore = (raw: unknown): GeneratorProfileFaceRestore | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const restore = raw as Partial<GeneratorProfileFaceRestore> & Record<string, unknown>;
  return {
    ...restore,
    enabled: Boolean(restore.enabled),
    fidelity: numberOr(restore.fidelity, 0.5),
  } as GeneratorProfileFaceRestore;
};

/**
 * `post` is deliberately shallow-normalized: known nested blocks
 * (face_detailer/face_restore/debug) get light coercion, but any additional
 * key the backend adds later rides through unchanged via the spread.
 */
const normalizePost = (raw: unknown): GeneratorProfilePost | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const post = raw as Partial<GeneratorProfilePost> & Record<string, unknown>;
  const faceDetailer = normalizeFaceDetailer(post.face_detailer);
  const faceRestore = normalizeFaceRestore(post.face_restore);
  return {
    ...post,
    ...(faceDetailer ? { face_detailer: faceDetailer } : {}),
    ...(faceRestore ? { face_restore: faceRestore } : {}),
    ...(typeof post.debug === 'boolean' ? { debug: post.debug } : {}),
  } as GeneratorProfilePost;
};

/** Editor layout hints are opaque to the backend contract — pass through untouched. */
const normalizeUI = (raw: unknown): GeneratorProfileUI | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  return { ...(raw as Record<string, unknown>) } as GeneratorProfileUI;
};

const normalizeSpec = (raw: unknown): GeneratorProfileSpec => {
  const spec = (raw || {}) as Partial<GeneratorProfileSpec>;
  const baseModel = (spec.base_model || {}) as Partial<GeneratorProfileBaseModel>;
  const prompt = (spec.prompt || {}) as Partial<GeneratorProfilePrompt>;
  const params = (spec.params || {}) as Partial<GeneratorProfileParams>;
  const adapters = Array.isArray(spec.adapters) ? spec.adapters : [];
  const controls = Array.isArray(spec.controls) ? spec.controls : [];
  const ipAdapter = spec.ip_adapter && typeof spec.ip_adapter === 'object'
    ? spec.ip_adapter
    : undefined;
  const embeddings = Array.isArray(spec.embeddings)
    ? spec.embeddings.map(normalizeEmbedding).filter((embedding): embedding is GeneratorProfileEmbedding => embedding !== null)
    : [];
  const vae = normalizeVAE(spec.vae);
  const post = normalizePost(spec.post);
  const ui = normalizeUI(spec.ui);

  const family: ModelFamily | undefined =
    baseModel.family === 'z-image' ? 'z-image' : baseModel.family === 'sdxl' ? 'sdxl' : undefined;
  const source = baseModel.source === 'civitai' ? 'civitai' : 'hf';

  const normalizedControls: GeneratorProfileControl[] = controls
    .filter((control): control is GeneratorProfileControl => (
      control?.type === 'openpose' || control?.type === 'softedge' || control?.type === 'depth'
    ))
    .slice(0, family === 'z-image' ? 1 : 2)
    .map((control, index) => {
      const conditioningPreset = family === 'z-image'
        ? control.conditioning_preset === 'exact_shape' || control.conditioning_preset === 'safe_pose'
          ? control.conditioning_preset
          : control.type === 'depth' ? 'exact_shape' : 'safe_pose'
        : undefined;
      return {
        id: typeof control.id === 'string' && control.id ? control.id : `control-${index + 1}`,
        type: control.type,
        ...(conditioningPreset ? { conditioning_preset: conditioningPreset } : {}),
        ...(family === 'z-image'
          ? { control_schedule: control.control_schedule === 'constant' ? 'constant' as const : 'structure_first' as const }
          : {}),
        ...(typeof control.model_id === 'string' && control.model_id ? { model_id: control.model_id } : {}),
        input_role: control.type === 'openpose' ? 'pose_guide' : control.type === 'depth' ? 'depth_guide' : 'body_guide',
        weight: numberOr(
          control.weight,
          conditioningPreset === 'exact_shape' ? 0.5 : control.type === 'openpose' ? 0.75 : control.type === 'depth' ? 0.85 : 0.3,
        ),
        start: numberOr(control.start, 0),
        end: numberOr(control.end, family === 'z-image' || control.type === 'depth' ? 1 : control.type === 'openpose' ? 0.75 : 0.5),
        required: control.required !== false,
        ...(normalizePuppetGuide(control.guide_config) ? { guide_config: normalizePuppetGuide(control.guide_config) } : {}),
      };
    });

  return {
    base_model: {
      source,
      id: typeof baseModel.id === 'string' ? baseModel.id : '',
      // Persist family when set, and always for civitai bases (defaulting to
      // sdxl there since family is non-derivable from a civitai id).
      ...(family ? { family } : source === 'civitai' ? { family: 'sdxl' as ModelFamily } : {}),
    },
    adapters: adapters
      .map((adapter) => ({
        artifact_id: typeof adapter?.artifact_id === 'string' ? adapter.artifact_id : '',
        weight: numberOr(adapter?.weight, 1),
        ...(typeof adapter?.enabled === 'boolean' ? { enabled: adapter.enabled } : {}),
      }))
      .filter((adapter) => adapter.artifact_id),
    ...(normalizedControls.length ? { controls: normalizedControls } : {}),
    ...(family !== 'z-image' && ipAdapter
      ? {
          ip_adapter: {
            input_role: 'ip_adapter_reference' as const,
            scale: Math.max(0, Math.min(1, numberOr(ipAdapter.scale, 0.3))),
            required: ipAdapter.required !== false,
          },
        }
      : {}),
    prompt: {
      base_prompt: typeof prompt.base_prompt === 'string' ? prompt.base_prompt : '',
      negative_prompt: typeof prompt.negative_prompt === 'string' ? prompt.negative_prompt : '',
      ...(prompt.weighting_enabled === true ? { weighting_enabled: true } : {}),
    },
    params: {
      width: numberOr(params.width, 1024),
      height: numberOr(params.height, 1024),
      steps: numberOr(params.steps, 28),
      cfg_scale: numberOr(params.cfg_scale, 7),
      scheduler: typeof params.scheduler === 'string' && params.scheduler ? params.scheduler : 'default',
      mode: params.mode === 'img2img' ? 'img2img' : params.mode === 'inpaint' ? 'inpaint' : 'txt2img',
      denoise_strength: clampDenoise(numberOr(params.denoise_strength, 0.65)),
      ...(typeof params.seed === 'number' && Number.isFinite(params.seed) ? { seed: params.seed } : {}),
      ...(typeof params.clip_skip === 'number' && Number.isFinite(params.clip_skip) ? { clip_skip: params.clip_skip } : {}),
    },
    ...(embeddings.length ? { embeddings } : {}),
    ...(vae ? { vae } : {}),
    ...(post ? { post } : {}),
    ...(ui ? { ui } : {}),
  };
};

const normalizeProfile = (raw: unknown): GeneratorProfile => {
  const profile = (raw || {}) as Partial<GeneratorProfile>;
  return {
    id: typeof profile.id === 'string' ? profile.id : '',
    slug: typeof profile.slug === 'string' ? profile.slug : '',
    version: numberOr(profile.version, 1),
    status: profile.status === 'published' ? 'published' : 'draft',
    user_id: typeof profile.user_id === 'string' ? profile.user_id : '',
    name: typeof profile.name === 'string' ? profile.name : '',
    media_type: 'image',
    spec: normalizeSpec(profile.spec),
    created_at: typeof profile.created_at === 'string' ? profile.created_at : '',
    updated_at: typeof profile.updated_at === 'string' ? profile.updated_at : '',
  };
};

/** kebab-case slug derived from a free-form name. */
export const slugifyName = (name: string): string =>
  (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

// ==================== Profile CRUD ====================

const listProfiles = (params: ListGeneratorProfilesParams = {}): Promise<GeneratorProfile[]> => {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.slug) query.set('slug', params.slug);
  const qs = query.toString();
  return axios
    .get<GeneratorProfile[]>(`${profilesPath()}${qs ? `?${qs}` : ''}`)
    .then((res) => (Array.isArray(res.data) ? res.data.map(normalizeProfile) : []));
};

const createProfile = (req: CreateGeneratorProfileRequest): Promise<GeneratorProfile> =>
  axios
    .post<GeneratorProfile>(profilesPath(), {
      slug: req.slug,
      name: req.name,
      media_type: 'image',
      spec: req.spec,
    } as CreateGeneratorProfileRequest)
    .then((res) => normalizeProfile(res.data));

const updateProfile = (id: string, req: UpdateGeneratorProfileRequest): Promise<GeneratorProfile> =>
  axios
    .put<GeneratorProfile>(profilesPath(`/${id}`), {
      ...(req.name !== undefined ? { name: req.name } : {}),
      ...(req.spec !== undefined ? { spec: req.spec } : {}),
    } as UpdateGeneratorProfileRequest)
    .then((res) => normalizeProfile(res.data));

const publishProfile = (id: string): Promise<GeneratorProfile> =>
  axios.post<GeneratorProfile>(profilesPath(`/${id}/publish`)).then((res) => normalizeProfile(res.data));

const deleteProfile = (id: string): Promise<{ status: string }> =>
  axios.delete<{ status: string }>(profilesPath(`/${id}`)).then((res) => res.data);

const listPuppetPoses = (): Promise<PuppetPoseLibraryItem[]> =>
  axios.get<PuppetPoseLibraryItem[]>(puppetPosesPath()).then((res) => (
    Array.isArray(res.data) ? res.data.map((item) => ({
      ...item,
      config: normalizePuppetGuide(item.config) || item.config,
    })) : []
  ));

const createPuppetPose = (
  name: string,
  promptHint: string,
  config: PuppetGuideConfig,
): Promise<PuppetPoseLibraryItem> => axios.post<PuppetPoseLibraryItem>(puppetPosesPath(), {
  name,
  prompt_hint: promptHint,
  config,
}).then((res) => res.data);

const updatePuppetPose = (
  id: string,
  name: string,
  promptHint: string,
  config: PuppetGuideConfig,
): Promise<PuppetPoseLibraryItem> => axios.put<PuppetPoseLibraryItem>(puppetPosesPath(`/${id}`), {
  name,
  prompt_hint: promptHint,
  config,
}).then((res) => res.data);

const deletePuppetPose = (id: string): Promise<void> =>
  axios.delete(puppetPosesPath(`/${id}`)).then(() => undefined);

// ==================== Civitai download (adapter ingest) ====================

/**
 * Triggers the Modal download for an imported Civitai record (import itself
 * goes through CivitaiAPI.createImport). This runs on Modal and may take tens
 * of seconds — callers must handle the pending state. On success the
 * resulting LoRAAdapterArtifact appears in GET /lora/artifacts.
 *
 * `kind` selects the bridge's download branch (default "lora" server-side);
 * pass 'embedding' for a textual-inversion import so it lands with
 * artifact_type 'civitai-embedding' instead of being mis-scaffolded as a LoRA.
 */
const downloadCivitaiImport = (importId: string, kind?: 'lora' | 'embedding'): Promise<LoRAAdapterArtifact> =>
  axios
    .post<LoRAAdapterArtifact>(`${BASE_URL}/civitai/imports/${importId}/download${kind ? `?kind=${kind}` : ''}`)
    .then((res) => res.data);

// ==================== Test generation ====================

/**
 * Runs an explicit GPU test generation against the current builder spec. The
 * inference provider is selected from the profile family (sdxl → Modal SDXL,
 * z-image → Modal Z-Image); civitai bases forward their family explicitly.
 *
 * `runtime_id`/`provider`/`init_image_ref` are optional pass-through
 * overrides — unused by the Generator Profile builder (which always targets
 * real Modal GPU inference) but read by the admin composite-inference smoke
 * tester, which can also target the bridge's local CPU scaffold. This same
 * request body posts to the same endpoint the bridge's
 * CompositeInferenceRequest schema accepts, so this client fully replaces
 * the former LoraRuntimeAPI.generateCompositeInference duplicate.
 */
const testGeneration = (req: ProfileTestGenerationRequest): Promise<ProfileTestGenerationResult> => {
  const hasInitImage = Boolean(req.init_image_b64) || Boolean(req.init_image_ref);
  const hasMaskImage = Boolean(req.mask_image_b64) || Boolean(req.mask_image_ref);
  const inpaint = req.mode === 'inpaint' && hasInitImage && hasMaskImage;
  const img2img = !inpaint && req.mode === 'img2img' && hasInitImage;
  // inpaint and img2img both carry the seed image + denoise strength; only
  // inpaint additionally carries the mask.
  const usesInitImage = inpaint || img2img;
  const family = normalizeFamily(req.family);
  return axios
    .post<ProfileTestGenerationResult>(`${BASE_URL}/lora/inference/generate`, {
      mode: inpaint ? 'inpaint' : img2img ? 'img2img' : 'txt2img',
      prompt: req.prompt,
      negative_prompt: req.negative_prompt,
      ...(req.prompt_weighting ? { prompt_weighting: true } : {}),
      base_model_id: req.base_model_id,
      base_model_family: family,
      adapter_ids: req.adapter_ids,
      lora_strengths: req.lora_strengths,
      width: req.width,
      height: req.height,
      steps: req.steps,
      cfg_scale: req.cfg_scale,
      scheduler: req.scheduler,
      ...(req.seed != null ? { seed: req.seed } : {}),
      ...(usesInitImage
        ? {
            ...(req.init_image_b64 ? { init_image_b64: stripDataUriPrefix(req.init_image_b64) } : {}),
            ...(req.init_image_ref ? { init_image_ref: req.init_image_ref } : {}),
            denoise_strength: clampDenoise(numberOr(req.denoise_strength, 0.65)),
          }
        : {}),
      ...(inpaint
        ? {
            ...(req.mask_image_b64 ? { mask_image_b64: stripDataUriPrefix(req.mask_image_b64) } : {}),
            ...(req.mask_image_ref ? { mask_image_ref: req.mask_image_ref } : {}),
          }
        : {}),
      ...(req.embeddings?.length ? { embeddings: req.embeddings } : {}),
      ...(req.vae_id ? { vae_id: req.vae_id } : {}),
      ...(req.clip_skip != null ? { clip_skip: req.clip_skip } : {}),
      ...(req.post ? { post: req.post } : {}),
      ...(req.gpu ? { gpu: req.gpu } : {}),
      ...(req.controls?.length
        ? {
            controls: req.controls.map((control) => ({
              ...control,
              guide_image_b64: stripDataUriPrefix(control.guide_image_b64),
            })),
          }
        : {}),
      ...(req.ip_adapter
        ? {
            ip_adapter: {
              ...req.ip_adapter,
              ...(req.ip_adapter.image_b64
                ? { image_b64: stripDataUriPrefix(req.ip_adapter.image_b64) }
                : {}),
            },
          }
        : {}),
      ...(req.profile_context ? { profile_context: req.profile_context } : {}),
      provider: req.provider ?? INFERENCE_PROVIDER[family],
      runtime_id: req.runtime_id ?? 'modal-python-gpu',
    })
    .then((res) => res.data);
};

/**
 * Flattens the diagnostics the backend reports on a failed test generation.
 * Reads top-level metadata first, then metadata.modal_result, preferring the
 * richer modal_result fields when both are present.
 */
export const extractTestDiagnostics = (
  result: ProfileTestGenerationResult | null
): ProfileTestDiagnostics => {
  if (!result?.metadata) return {};
  const top = result.metadata as Record<string, unknown>;
  const modalResult = (top.modal_result || {}) as Record<string, unknown>;
  const pick = (key: string): unknown => modalResult[key] ?? top[key];

  return {
    failure_reason: typeof pick('failure_reason') === 'string' ? (pick('failure_reason') as string) : undefined,
    retry_reason: typeof pick('retry_reason') === 'string' ? (pick('retry_reason') as string) : undefined,
    vae_fix_model: typeof pick('vae_fix_model') === 'string' ? (pick('vae_fix_model') as string) : undefined,
    vae_fix_error: typeof pick('vae_fix_error') === 'string' ? (pick('vae_fix_error') as string) : undefined,
    image_stats: (pick('image_stats') as ProfileTestImageStats) || undefined,
    first_pass_stats: (pick('first_pass_stats') as ProfileTestImageStats) || undefined,
  };
};

/** Reads the bounded SDXL/Z-Image puppet-adherence check from completed-run metadata. */
export const extractPoseQualityGuard = (
  result: ProfileTestGenerationResult | null | undefined
): ProfileTestPoseQualityGuard | null => {
  const raw = result?.metadata?.quality_guard;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const guard = raw as ProfileTestPoseQualityGuard;
  return Array.isArray(guard.attempts) ? guard : null;
};

/**
 * Reads the post-chain's debug sample refs. Unlike `post` (a documented
 * top-level `CompositeInferenceResult` field the Go backend relays
 * unmodified), `sample_refs_debug` isn't promoted there — it's nested inside
 * `metadata.modal_result.sample_refs_debug` (the raw Modal-function
 * passthrough the bridge stores at `metadata.modal_result`). Empty when
 * `post.debug` wasn't set, no stage ran, or the result predates the field.
 * Accepts either a `ProfileTestGenerationResult` or a persisted media item's
 * `metadata` object directly (GenerationsRail reads the latter).
 */
export const extractSampleRefsDebug = (
  source: ProfileTestGenerationResult | Record<string, unknown> | null | undefined
): string[] => {
  if (!source) return [];
  const metadata = ('metadata' in source ? source.metadata : source) as Record<string, unknown> | undefined;
  if (!metadata) return [];
  // GenerationsRail's persisted copy stores sample_refs_debug at the top of
  // the media item's metadata (see persistInferenceTestGeneration); the live
  // test-generation result only ever has it nested under modal_result.
  const direct = metadata.sample_refs_debug;
  if (Array.isArray(direct)) return direct.filter((ref): ref is string => typeof ref === 'string');
  const modalResult = metadata.modal_result as Record<string, unknown> | undefined;
  const nested = modalResult?.sample_refs_debug;
  return Array.isArray(nested) ? nested.filter((ref): ref is string => typeof ref === 'string') : [];
};

/**
 * Reads the settled generation cost (in credits) from a test-generation result
 * or a persisted media item's `metadata`. Returns undefined when absent or
 * non-finite so callers omit the row rather than render NaN. Mirrors
 * media.ts's readCostCredits (kept local so this module has no api/media dep).
 */
export const extractCostCredits = (
  source: ProfileTestGenerationResult | Record<string, unknown> | null | undefined
): number | undefined => {
  if (!source) return undefined;
  const metadata = ('metadata' in source ? source.metadata : source) as Record<string, unknown> | undefined;
  if (!metadata) return undefined;
  const raw = metadata.cost_credits;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

// ==================== Export ====================

const GeneratorProfilesAPI = {
  listProfiles,
  createProfile,
  updateProfile,
  publishProfile,
  deleteProfile,
  listPuppetPoses,
  createPuppetPose,
  updatePuppetPose,
  deletePuppetPose,
  downloadCivitaiImport,
  testGeneration,
};

export default GeneratorProfilesAPI;
